// src/utils/columnDetection.js
import * as THREE from 'three';

/**
 * Kolon Algılama Utility
 * Pattern hatch'leri analiz ederek kolonları tespit eder
 */

const COLUMN_CONFIG = {
  // Kolon ölçü aralıkları (cm cinsinden)
  MIN_SHORT_EDGE: 25,
  MAX_SHORT_EDGE: 45,
  MIN_LONG_EDGE: 35,
  MAX_LONG_EDGE: 80,
  // Minimum aynı ölçüde kolon sayısı
  MIN_SAME_SIZE_COUNT: 4,
  // Ölçü toleransı (cm)
  SIZE_TOLERANCE: 2,
};

/**
 * Boundary path'ten köşe noktalarını çıkarır
 * @param {Object} hatchObj - Hatch THREE.js objesi
 * @returns {Array|null} - Köşe noktaları [{x, y}, ...] veya null
 */
function extractBoundaryPoints(hatchObj) {
  const userData = hatchObj.userData;

  // boundaryPaths veya boundaryPath kontrolü
  const boundaryPaths = userData.boundaryPaths || userData.boundaryPath;
  if (!boundaryPaths || !Array.isArray(boundaryPaths)) return null;

  // İlk outer boundary'yi bul
  const outerBoundary = boundaryPaths.find(bp => bp.type === 'outer');
  if (!outerBoundary || !outerBoundary.groups) return null;

  const points = [];

  outerBoundary.groups.forEach(group => {
    if (Array.isArray(group)) {
      group.forEach(segment => {
        extractPointsFromSegment(segment, points);
      });
    } else {
      extractPointsFromSegment(group, points);
    }
  });

  return points.length >= 4 ? points : null;
}

/**
 * Segment'ten noktaları çıkarır
 */
function extractPointsFromSegment(segment, points) {
  if (!segment) return;

  if (segment.edgeType === 'Line') {
    if (segment.x1 !== undefined && segment.y1 !== undefined) {
      points.push({ x: segment.x1, y: segment.y1 });
    }
    if (segment.x2 !== undefined && segment.y2 !== undefined) {
      points.push({ x: segment.x2, y: segment.y2 });
    }
  } else if (segment.edgeType === 'Polyline' && segment.points) {
    for (let i = 0; i < segment.points.length; i += 2) {
      points.push({ x: segment.points[i], y: segment.points[i + 1] });
    }
  }
}

/**
 * Noktaları unique hale getir (çok yakın olanları birleştir)
 */
function uniquePoints(points, tolerance = 0.5) {
  const unique = [];
  for (const p of points) {
    const exists = unique.some(u =>
      Math.abs(u.x - p.x) < tolerance && Math.abs(u.y - p.y) < tolerance
    );
    if (!exists) {
      unique.push(p);
    }
  }
  return unique;
}

/**
 * 4 köşeli dikdörtgenin kenar uzunluklarını hesaplar
 * @param {Array} points - 4 köşe noktası
 * @returns {Object|null} - { shortEdge, longEdge, center, points } veya null
 */
function calculateRectangleEdges(points) {
  if (points.length !== 4) return null;

  // Kenar uzunluklarını hesapla
  const edges = [];
  for (let i = 0; i < 4; i++) {
    const p1 = points[i];
    const p2 = points[(i + 1) % 4];
    const length = Math.sqrt(
      Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2)
    );
    edges.push(length);
  }

  // Karşılıklı kenarların eşit olup olmadığını kontrol et (tolerans ile)
  const tolerance = COLUMN_CONFIG.SIZE_TOLERANCE;
  const edge1 = edges[0];
  const edge2 = edges[1];
  const edge3 = edges[2];
  const edge4 = edges[3];

  // Karşılıklı kenarlar yaklaşık eşit olmalı
  if (Math.abs(edge1 - edge3) > tolerance || Math.abs(edge2 - edge4) > tolerance) {
    return null;
  }

  // Kısa ve uzun kenarları belirle
  const avgEdge1 = (edge1 + edge3) / 2;
  const avgEdge2 = (edge2 + edge4) / 2;

  const shortEdge = Math.min(avgEdge1, avgEdge2);
  const longEdge = Math.max(avgEdge1, avgEdge2);

  // Merkez noktayı hesapla
  const center = {
    x: points.reduce((sum, p) => sum + p.x, 0) / 4,
    y: points.reduce((sum, p) => sum + p.y, 0) / 4,
  };

  return { shortEdge, longEdge, center, points };
}

/**
 * Hatch'in pattern (solid değil) olup olmadığını kontrol eder
 */
function isPatternHatch(hatchObj) {
  const userData = hatchObj.userData;

  // Solid fill değilse pattern'dir
  if (userData.SolidFillFlag === 1 && userData.HatchPatternName === 'SOLID') {
    return false;
  }

  // isPatternHatch flag'i varsa
  if (userData.isPatternHatch === true) {
    return true;
  }

  // Pattern name varsa ve SOLID değilse
  if (userData.HatchPatternName && userData.HatchPatternName !== 'SOLID') {
    return true;
  }

  return false;
}

/**
 * Bir floor plan group içindeki tüm hatch'leri bulur
 */
function findAllHatches(group) {
  const hatches = [];

  group.traverse((obj) => {
    const type = obj.userData?.type?.toLowerCase?.() || obj.userData?.type;
    if (type === 'hatch' || type === 'HATCH') {
      hatches.push(obj);
    }
  });

  return hatches;
}

/**
 * Ölçüleri karşılaştırır (tolerans ile)
 */
function sizeMatches(size1, size2, tolerance = COLUMN_CONFIG.SIZE_TOLERANCE) {
  return Math.abs(size1.shortEdge - size2.shortEdge) <= tolerance &&
         Math.abs(size1.longEdge - size2.longEdge) <= tolerance;
}

/**
 * Floor plan group içindeki kolonları tespit eder
 * @param {THREE.Group} floorPlanGroup - Kat planı grubu
 * @returns {Array} - Tespit edilen kolonlar [{center, shortEdge, longEdge, hatchObj, points}, ...]
 */
export function detectColumns(floorPlanGroup) {
  if (!floorPlanGroup) return [];

  // 1. Tüm hatch'leri bul
  const allHatches = findAllHatches(floorPlanGroup);

  // 2. Pattern hatch'leri filtrele
  const patternHatches = allHatches.filter(isPatternHatch);

  // 3. Her hatch için boundary'yi analiz et
  const candidates = [];

  for (const hatch of patternHatches) {
    // Boundary noktalarını çıkar (local koordinatlarda)
    let points = extractBoundaryPoints(hatch);
    if (!points) continue;

    // Unique noktalar
    points = uniquePoints(points);

    // 4 köşeli mi kontrol et
    if (points.length !== 4) continue;

    // Kenar uzunluklarını hesapla (local koordinatlarda - ölçüler için)
    const rectInfo = calculateRectangleEdges(points);
    if (!rectInfo) continue;

    // Ölçü aralığında mı kontrol et
    const { shortEdge, longEdge, center } = rectInfo;

    if (shortEdge < COLUMN_CONFIG.MIN_SHORT_EDGE ||
        shortEdge > COLUMN_CONFIG.MAX_SHORT_EDGE ||
        longEdge < COLUMN_CONFIG.MIN_LONG_EDGE ||
        longEdge > COLUMN_CONFIG.MAX_LONG_EDGE) {
      continue;
    }

    // Hatch'in world pozisyonunu hesapla
    hatch.updateMatrixWorld(true);

    let worldCenter;
    let worldPoints;

    // Hatch'in bounding box'ını hesapla (THREE.Box3 kullanarak tüm children dahil)
    const box3 = new THREE.Box3();
    box3.setFromObject(hatch);

    if (!box3.isEmpty()) {
      // Box3 köşelerini world koordinatlarında döndürür
      const min = box3.min;
      const max = box3.max;

      worldPoints = [
        { x: min.x, y: min.y },
        { x: max.x, y: min.y },
        { x: max.x, y: max.y },
        { x: min.x, y: max.y },
      ];

      const boxCenter = new THREE.Vector3();
      box3.getCenter(boxCenter);
      worldCenter = { x: boxCenter.x, y: boxCenter.y };
    } else {
      // Fallback: sadece hatch.position kullan
      const hatchWorldPos = new THREE.Vector3();
      hatch.getWorldPosition(hatchWorldPos);

      // Boundary points'i hatch world pozisyonuna göre offset ekle
      // Boundary noktaları muhtemelen (0,0) merkezli, width/height boyutunda
      const halfWidth = shortEdge / 2;
      const halfHeight = longEdge / 2;

      worldCenter = { x: hatchWorldPos.x, y: hatchWorldPos.y };
      worldPoints = [
        { x: hatchWorldPos.x - halfWidth, y: hatchWorldPos.y - halfHeight },
        { x: hatchWorldPos.x + halfWidth, y: hatchWorldPos.y - halfHeight },
        { x: hatchWorldPos.x + halfWidth, y: hatchWorldPos.y + halfHeight },
        { x: hatchWorldPos.x - halfWidth, y: hatchWorldPos.y + halfHeight },
      ];
    }

    candidates.push({
      shortEdge: Math.round(shortEdge * 10) / 10,
      longEdge: Math.round(longEdge * 10) / 10,
      center: worldCenter,
      hatchObj: hatch,
      points: worldPoints,
    });
  }

  // 4. Aynı ölçüdeki hatch'leri grupla
  const sizeGroups = [];

  for (const candidate of candidates) {
    let foundGroup = false;

    for (const group of sizeGroups) {
      if (sizeMatches(group[0], candidate)) {
        group.push(candidate);
        foundGroup = true;
        break;
      }
    }

    if (!foundGroup) {
      sizeGroups.push([candidate]);
    }
  }

  // 5. En az 4 aynı ölçüde olan grupları kolon olarak kabul et
  const columns = [];

  for (const group of sizeGroups) {
    if (group.length >= COLUMN_CONFIG.MIN_SAME_SIZE_COUNT) {
      columns.push(...group);
    }
  }

  return columns;
}

/**
 * Tespit edilen kolonları görselleştirir (şeffaf overlay)
 * @param {THREE.Scene} scene - THREE.js sahne
 * @param {Array} columns - detectColumns'dan dönen kolon listesi
 * @param {number} color - Overlay rengi (varsayılan: cyan)
 * @param {number} opacity - Şeffaflık (varsayılan: 0.5)
 * @returns {THREE.Group} - Overlay grupları
 */
export function visualizeColumns(scene, columns, color = 0x00ffff, opacity = 0.5) {
  const overlayGroup = new THREE.Group();
  overlayGroup.name = 'column-overlays';
  overlayGroup.userData = { isColumnOverlay: true };

  for (const column of columns) {
    const { points } = column;

    // Shape oluştur
    const shape = new THREE.Shape();
    shape.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      shape.lineTo(points[i].x, points[i].y);
    }
    shape.lineTo(points[0].x, points[0].y);

    // Mesh oluştur
    const geometry = new THREE.ShapeGeometry(shape);
    const material = new THREE.MeshBasicMaterial({
      color: color,
      transparent: true,
      opacity: opacity,
      side: THREE.DoubleSide,
      depthWrite: false,
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.z = 0.1; // Hatch'in üzerinde görünsün
    mesh.userData = {
      isColumnMarker: true,
      columnData: column,
    };

    overlayGroup.add(mesh);
  }

  scene.add(overlayGroup);
  return overlayGroup;
}

/**
 * Kolon overlay'larını temizler
 */
export function clearColumnOverlays(scene) {
  const toRemove = [];
  scene.traverse((obj) => {
    if (obj.userData?.isColumnOverlay || obj.name === 'column-overlays') {
      toRemove.push(obj);
    }
  });

  for (const obj of toRemove) {
    if (obj.parent) {
      obj.parent.remove(obj);
    }
    if (obj.geometry) obj.geometry.dispose();
    if (obj.material) obj.material.dispose();
  }
}

/**
 * İki kat planı arasındaki kolon eşleşmesini bulur ve offset hesaplar
 * @param {Array} columns1 - İlk kat planı kolonları
 * @param {Array} columns2 - İkinci kat planı kolonları
 * @returns {Object|null} - { offsetX, offsetY, matchCount } veya null
 */
export function calculateFloorOffset(columns1, columns2) {
  if (columns1.length < 4 || columns2.length < 4) {
    console.warn('[Column Detection] Not enough columns to calculate offset');
    return null;
  }

  // Her iki kat planındaki kolonların merkez noktalarını al
  const centers1 = columns1.map(c => c.center);
  const centers2 = columns2.map(c => c.center);

  // Her olası offset için eşleşme sayısını hesapla
  const offsetCandidates = [];

  for (const c1 of centers1) {
    for (const c2 of centers2) {
      const offsetX = c2.x - c1.x;
      const offsetY = c2.y - c1.y;

      // Bu offset ile kaç kolon eşleşiyor?
      let matchCount = 0;
      for (const col1 of centers1) {
        const expectedPos = {
          x: col1.x + offsetX,
          y: col1.y + offsetY,
        };

        const hasMatch = centers2.some(col2 =>
          Math.abs(col2.x - expectedPos.x) < COLUMN_CONFIG.SIZE_TOLERANCE * 2 &&
          Math.abs(col2.y - expectedPos.y) < COLUMN_CONFIG.SIZE_TOLERANCE * 2
        );

        if (hasMatch) matchCount++;
      }

      if (matchCount >= 4) {
        offsetCandidates.push({ offsetX, offsetY, matchCount });
      }
    }
  }

  // En çok eşleşen offset'i bul
  if (offsetCandidates.length === 0) {
    console.warn('[Column Detection] No matching offset found');
    return null;
  }

  offsetCandidates.sort((a, b) => b.matchCount - a.matchCount);
  const bestOffset = offsetCandidates[0];

  return bestOffset;
}

export { COLUMN_CONFIG };

// ═══════════════════════════════════════════════════════════════════════════
// MERDİVEN (STAIR) TESPİTİ
// Kolon bulunmayan yapılarda kat planları arasındaki offset'i hesaplamak için
// kullanılır. Merdivenin karakteristik yapısı:
//   • 4+ paralel çizgi (basamaklar)
//   • Birbirine eşit mesafede (aritmetik seri)
//   • Benzer uzunlukta
// Tespit strateji: scene içindeki line segmentlerini paralellik ve eşit aralık
// ile kümelendir; her küme bir merdiven.
// ═══════════════════════════════════════════════════════════════════════════

const STAIR_CONFIG = {
  MIN_STEPS: 3,                 // Bir kolda minimum basamak sayısı
  MAX_STEPS: 30,                // Üst sınır (aşırı uzun parallel duvar hattı kaçmasın)
  MIN_STEP_LENGTH: 80,          // cm — basamak minimum uzunluğu
  MAX_STEP_LENGTH: 240,         // cm — basamak maksimum uzunluğu
  MIN_STEP_SPACING: 20,         // cm — basamaklar arası minimum mesafe
  MAX_STEP_SPACING: 40,         // cm — basamaklar arası maksimum mesafe
  ANGLE_TOLERANCE_DEG: 3,       // paralellik toleransı (derece)
  LENGTH_TOLERANCE_RATIO: 0.10, // basamak uzunluğu farkı %10 (aynı boyda olmalı)
  SPACING_TOLERANCE_RATIO: 0.15, // eşit aralık toleransı %15
  // U-motif parametreleri: iki paralel basamak grubu arasında min boşluk
  MIN_U_GAP: 40,                // cm — iki kol arası min perpendicular mesafe
  MAX_U_GAP: 400,               // cm — iki kol arası max mesafe (çok uzak paraleller kaçmasın)
};

// Tüm Line segmentlerini toplar (düz çizgiler, polyline segmentleri dahil).
function collectLineSegments(floorPlanGroup) {
  const segments = [];
  floorPlanGroup.updateMatrixWorld(true);
  floorPlanGroup.traverse(obj => {
    if (!obj.isLine && !obj.isLineSegments && !obj.isLineLoop) return;
    if (obj.userData?.type === 'wire') return;            // tesisat hattı
    if (obj.userData?.isPanelPart) return;
    if (obj.userData?.isOffsetPreview) return;
    const geom = obj.geometry;
    const pos = geom?.attributes?.position;
    if (!pos || pos.count < 2) return;
    const world = obj.matrixWorld;
    const v = new THREE.Vector3();
    for (let i = 0; i < pos.count - 1; i++) {
      v.set(pos.getX(i), pos.getY(i), pos.getZ(i)).applyMatrix4(world);
      const x1 = v.x, y1 = v.y;
      v.set(pos.getX(i + 1), pos.getY(i + 1), pos.getZ(i + 1)).applyMatrix4(world);
      const x2 = v.x, y2 = v.y;
      const dx = x2 - x1, dy = y2 - y1;
      const len = Math.hypot(dx, dy);
      if (len < STAIR_CONFIG.MIN_STEP_LENGTH || len > STAIR_CONFIG.MAX_STEP_LENGTH) continue;
      // Açıyı [0, 180) aralığına indirge — aynı yöndeki paralelleri eşleştirmek için
      let angle = Math.atan2(dy, dx) * 180 / Math.PI;
      if (angle < 0) angle += 180;
      segments.push({
        x1, y1, x2, y2,
        mx: (x1 + x2) / 2,
        my: (y1 + y2) / 2,
        length: len,
        angle,
      });
    }
  });
  return segments;
}

// İki açı arasındaki dairesel fark (180°'lik döngüde, yani yön işareti önemsiz).
function angleDiff(a, b) {
  let d = Math.abs(a - b) % 180;
  if (d > 90) d = 180 - d;
  return d;
}

// Bir noktanın belirli yöndeki çizgi ailesine perpendicular offset'i.
function perpOffset(point, angleDeg) {
  const rad = angleDeg * Math.PI / 180;
  // Normal vector (dik yön)
  const nx = -Math.sin(rad), ny = Math.cos(rad);
  return point.x * nx + point.y * ny;
}

// Tek kolda eşit aralıklı paralel basamak kümelerini bulur.
function findFlightsInAngleBin(withOffset) {
  const flights = [];
  for (let start = 0; start <= withOffset.length - STAIR_CONFIG.MIN_STEPS; start++) {
    const cluster = [withOffset[start]];
    for (let j = start + 1; j < withOffset.length; j++) {
      const last = cluster[cluster.length - 1];
      const gap = withOffset[j].offset - last.offset;
      if (gap < STAIR_CONFIG.MIN_STEP_SPACING) continue;
      if (gap > STAIR_CONFIG.MAX_STEP_SPACING) break;
      const refLen = cluster[0].seg.length;
      if (Math.abs(withOffset[j].seg.length - refLen) / refLen > STAIR_CONFIG.LENGTH_TOLERANCE_RATIO) continue;
      if (cluster.length >= 2) {
        const firstGap = cluster[1].offset - cluster[0].offset;
        if (Math.abs(gap - firstGap) / firstGap > STAIR_CONFIG.SPACING_TOLERANCE_RATIO) continue;
      }
      cluster.push(withOffset[j]);
    }
    if (cluster.length < STAIR_CONFIG.MIN_STEPS || cluster.length > STAIR_CONFIG.MAX_STEPS) continue;

    // Flight geometrisi
    let cx = 0, cy = 0;
    for (const c of cluster) { cx += c.seg.mx; cy += c.seg.my; }
    cx /= cluster.length; cy /= cluster.length;
    const totalSpan = cluster[cluster.length - 1].offset - cluster[0].offset;
    const spacing = totalSpan / (cluster.length - 1);
    const avgLen = cluster.reduce((s, c) => s + c.seg.length, 0) / cluster.length;
    flights.push({
      cluster,
      center: { x: cx, y: cy },
      minOffset: cluster[0].offset,
      maxOffset: cluster[cluster.length - 1].offset,
      stepCount: cluster.length,
      stepLength: Math.round(avgLen * 10) / 10,
      spacing: Math.round(spacing * 10) / 10,
      firstMid: { x: cluster[0].seg.mx, y: cluster[0].seg.my },
      lastMid: { x: cluster[cluster.length - 1].seg.mx, y: cluster[cluster.length - 1].seg.my },
    });
    start += cluster.length - 1;
  }
  return flights;
}

/**
 * Kat planında merdiven yapılarını tespit eder.
 * Öncelik: U-motif (iki paralel kol + gap). Bulunamazsa tek-kol flight'lar
 * da merdiven sayılır (fallback) — prefab veya tek-kol merdivenler için.
 * @param {THREE.Group} floorPlanGroup
 * @returns {Array}
 */
export function detectStairs(floorPlanGroup) {
  if (!floorPlanGroup) return [];
  const segments = collectLineSegments(floorPlanGroup);
  if (segments.length < STAIR_CONFIG.MIN_STEPS) return [];

  // 1) Açıya göre grupla (paralel çizgileri topla)
  const angleBins = [];
  for (const s of segments) {
    let bin = angleBins.find(b => angleDiff(b.angle, s.angle) < STAIR_CONFIG.ANGLE_TOLERANCE_DEG);
    if (!bin) { bin = { angle: s.angle, items: [] }; angleBins.push(bin); }
    bin.items.push(s);
  }

  // Tüm angle bin'lerdeki flight'ları topla — fallback için gerekli
  const binFlights = [];
  for (const bin of angleBins) {
    if (bin.items.length < STAIR_CONFIG.MIN_STEPS) continue;
    const withOffset = bin.items.map(s => ({
      seg: s, offset: perpOffset({ x: s.mx, y: s.my }, bin.angle),
    })).sort((a, b) => a.offset - b.offset);
    const flights = findFlightsInAngleBin(withOffset);
    if (flights.length > 0) binFlights.push({ angle: bin.angle, flights });
  }

  const stairs = [];
  for (const { angle, flights } of binFlights) {
    if (flights.length < 2) continue;
    const bin = { angle, items: null }; // sadece angle gerekli

    // 2) Aynı açıdaki flight çiftlerini U-motif için eşleştir.
    //    Her çift için: aynı basamak sayısı, aynı uzunluk, aynı spacing, ve
    //    aralarında MIN_U_GAP..MAX_U_GAP perpendicular boşluk olmalı.
    const used = new Set();
    for (let i = 0; i < flights.length; i++) {
      if (used.has(i)) continue;
      for (let j = i + 1; j < flights.length; j++) {
        if (used.has(j)) continue;
        const A = flights[i], B = flights[j];
        if (Math.abs(A.stepCount - B.stepCount) > 1) continue;
        if (Math.abs(A.stepLength - B.stepLength) / A.stepLength > STAIR_CONFIG.LENGTH_TOLERANCE_RATIO) continue;
        if (Math.abs(A.spacing - B.spacing) / A.spacing > STAIR_CONFIG.SPACING_TOLERANCE_RATIO) continue;

        // Gap = iki kol arasındaki perpendicular boşluk (iç kenarlar arası)
        const gap = B.minOffset - A.maxOffset; // flights sıralı olduğu için ≥ 0
        if (gap < STAIR_CONFIG.MIN_U_GAP || gap > STAIR_CONFIG.MAX_U_GAP) continue;

        // U-merdiven oluştu
        const cx = (A.center.x + B.center.x) / 2;
        const cy = (A.center.y + B.center.y) / 2;
        const allSegs = [...A.cluster, ...B.cluster].map(c => c.seg);
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (const s of allSegs) {
          minX = Math.min(minX, s.x1, s.x2);
          maxX = Math.max(maxX, s.x1, s.x2);
          minY = Math.min(minY, s.y1, s.y2);
          maxY = Math.max(maxY, s.y1, s.y2);
        }

        stairs.push({
          center: { x: cx, y: cy },
          angle: bin.angle,
          stepCount: A.stepCount + B.stepCount,
          stepLength: Math.round(((A.stepLength + B.stepLength) / 2) * 10) / 10,
          spacing: Math.round(((A.spacing + B.spacing) / 2) * 10) / 10,
          uGap: Math.round(gap * 10) / 10,
          // İki kolun merkezleri — cable shaft'ın hangi kola yakın olduğunu
          // saptamak ve yansıtmada aynı kolu kullanmak için.
          flightA: { center: A.center, firstMid: A.firstMid, lastMid: A.lastMid, stepCount: A.stepCount },
          flightB: { center: B.center, firstMid: B.firstMid, lastMid: B.lastMid, stepCount: B.stepCount },
          // Dış uç noktaları — U'nun ağzı/tabanı referansı (her iki katta stabil)
          anchorOuterA: A.firstMid,  // A'nın dış ucu
          anchorOuterB: B.lastMid,   // B'nin dış ucu
          anchorStart: A.firstMid,   // backward compat
          anchorEnd: B.lastMid,
          bounds: { min: { x: minX, y: minY }, max: { x: maxX, y: maxY } },
          segments: allSegs,
        });
        used.add(i); used.add(j);
        break; // i'yi bir B ile eşleştirdik, j'yi de kullanılmış say
      }
    }
  }

  // ─── Fallback: U-motif bulunamazsa tek-kol flight'ları merdiven say ───
  // (Prefab / tek-kol merdivenler veya U'nun bir kolu eksik kaldığında).
  if (stairs.length === 0) {
    for (const { angle, flights } of binFlights) {
      for (const f of flights) {
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (const c of f.cluster) {
          minX = Math.min(minX, c.seg.x1, c.seg.x2);
          maxX = Math.max(maxX, c.seg.x1, c.seg.x2);
          minY = Math.min(minY, c.seg.y1, c.seg.y2);
          maxY = Math.max(maxY, c.seg.y1, c.seg.y2);
        }
        stairs.push({
          center: { x: f.center.x, y: f.center.y },
          angle,
          stepCount: f.stepCount,
          stepLength: f.stepLength,
          spacing: f.spacing,
          uGap: null, // tek kol — U yok
          flightA: { center: f.center, firstMid: f.firstMid, lastMid: f.lastMid, stepCount: f.stepCount },
          flightB: null,
          anchorOuterA: f.firstMid,
          anchorOuterB: f.lastMid,
          anchorStart: f.firstMid,
          anchorEnd: f.lastMid,
          bounds: { min: { x: minX, y: minY }, max: { x: maxX, y: maxY } },
          segments: f.cluster.map(c => c.seg),
          _fallback: true,
        });
      }
    }
  }

  // Duplicate eliminasyonu — merkezleri çok yakın olanlar tek tutulur
  const unique = [];
  for (const s of stairs) {
    const dup = unique.find(u =>
      Math.hypot(u.center.x - s.center.x, u.center.y - s.center.y) < 50 &&
      angleDiff(u.angle, s.angle) < STAIR_CONFIG.ANGLE_TOLERANCE_DEG
    );
    if (!dup) unique.push(s);
  }
  return unique;
}

/**
 * İki merdivenin fingerprint (parmak izi) ile eşleşip eşleşmediğini döner.
 * U-motif: toplam basamak sayısı + basamak uzunluğu + aralık + U-gap.
 */
function stairsMatch(s1, s2) {
  if (angleDiff(s1.angle, s2.angle) > STAIR_CONFIG.ANGLE_TOLERANCE_DEG * 2) return false;
  if (Math.abs(s1.stepCount - s2.stepCount) > 2) return false;
  if (Math.abs(s1.spacing - s2.spacing) / s1.spacing > STAIR_CONFIG.SPACING_TOLERANCE_RATIO) return false;
  if (Math.abs(s1.stepLength - s2.stepLength) / s1.stepLength > STAIR_CONFIG.LENGTH_TOLERANCE_RATIO) return false;
  if (s1.uGap != null && s2.uGap != null) {
    if (Math.abs(s1.uGap - s2.uGap) / Math.max(s1.uGap, 1) > 0.25) return false;
  }
  return true;
}

/**
 * Belirli bir merdivene karşılık gelen hedef kattaki merdiveni bulur.
 * (Fingerprint en iyi uyanı seçer.)
 * @param {Object} stair — kaynak merdiven
 * @param {Array} candidates — hedef kat merdivenleri
 * @returns {Object|null}
 */
export function findCorrespondingStair(stair, candidates) {
  let best = null;
  let bestScore = -Infinity;
  for (const c of candidates) {
    if (!stairsMatch(stair, c)) continue;
    // Skor: step count + spacing + length farklarından
    const score =
      -Math.abs(stair.stepCount - c.stepCount) * 2
      - Math.abs(stair.stepLength - c.stepLength) / Math.max(stair.stepLength, 1) * 5
      - Math.abs(stair.spacing - c.spacing) / Math.max(stair.spacing, 1) * 5;
    if (score > bestScore) {
      bestScore = score;
      best = c;
    }
  }
  return best;
}

/**
 * İki kat planındaki merdivenleri kullanarak offset hesaplar (fallback kullanım).
 * Birden fazla merdiven varsa en çok tutarlı olan offset'i seçer.
 * @param {Array} stairs1 — mevcut kat
 * @param {Array} stairs2 — hedef kat
 * @returns {{offsetX:number, offsetY:number, matchCount:number}|null}
 */
export function calculateFloorOffsetByStairs(stairs1, stairs2) {
  if (!stairs1.length || !stairs2.length) return null;

  const candidates = [];
  for (const s1 of stairs1) {
    for (const s2 of stairs2) {
      if (!stairsMatch(s1, s2)) continue;
      const offsetX = s2.center.x - s1.center.x;
      const offsetY = s2.center.y - s1.center.y;
      // Kaç başka stair çifti bu offset ile uyuşuyor?
      let matchCount = 0;
      for (const a of stairs1) {
        const expected = { x: a.center.x + offsetX, y: a.center.y + offsetY };
        const hit = stairs2.some(b => stairsMatch(a, b) &&
          Math.abs(b.center.x - expected.x) < 30 &&
          Math.abs(b.center.y - expected.y) < 30);
        if (hit) matchCount++;
      }
      candidates.push({ offsetX, offsetY, matchCount, s1, s2 });
    }
  }
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => b.matchCount - a.matchCount);
  return candidates[0];
}

export { STAIR_CONFIG };

/**
 * Bir kat planındaki world noktasını başka bir katın kat planı ÇİZİMİNE göre
 * (frame/mesh değil) karşılık gelen world noktasına projeksiyonlar.
 *
 * Kablo bacasının kat-karşılığı bulma yöntemiyle AYNI mantık (useCableShaft):
 * en yakın kolonu/merdiveni referans alır, offset'i o referansa göre hesaplar,
 * hedef katta karşılık gelen referansı (kolon dizilimini karşılaştırarak) bulup
 * aynı offset'i uygular. Böylece çizimin mesh içindeki ötelenmesi telafi edilir.
 *
 * @param {THREE.Object3D} sourceFpMesh - kaynak kat planı mesh'i
 * @param {THREE.Object3D} targetFpMesh - hedef kat planı mesh'i
 * @param {{x:number,y:number}} worldPos - kaynak kattaki world konumu
 * @returns {{x:number,y:number,z:number}|null} hedef katta karşılık gelen world konum (bulunamazsa null → caller fallback yapsın)
 */
export function projectPointAcrossFloorMeshes(sourceFpMesh, targetFpMesh, worldPos) {
  if (!sourceFpMesh || !targetFpMesh || !worldPos) return null;
  sourceFpMesh.updateWorldMatrix(true, false);
  targetFpMesh.updateWorldMatrix(true, false);

  const toLocal = (mesh, p) => {
    const v = new THREE.Vector3(p.x, p.y, 0);
    mesh.worldToLocal(v);
    return { x: v.x, y: v.y };
  };
  const columnsToLocal = (mesh, cols) => cols.map(c => ({ ...c, center: toLocal(mesh, c.center) }));
  const stairsToLocal = (mesh, stairs) => stairs.map(s => ({
    ...s,
    center: toLocal(mesh, s.center),
    anchorStart: s.anchorStart ? toLocal(mesh, s.anchorStart) : undefined,
    anchorEnd: s.anchorEnd ? toLocal(mesh, s.anchorEnd) : undefined,
  }));

  const clickLocal = toLocal(sourceFpMesh, worldPos);

  const currentColumns = columnsToLocal(sourceFpMesh, detectColumns(sourceFpMesh));
  const useStairs = currentColumns.length < 4;
  const currentStairs = useStairs ? stairsToLocal(sourceFpMesh, detectStairs(sourceFpMesh)) : [];

  const refPoints = useStairs ? currentStairs : currentColumns;
  if (!refPoints.length) return null;

  const nearestRef = [...refPoints].sort((a, b) =>
    Math.hypot(a.center.x - clickLocal.x, a.center.y - clickLocal.y)
    - Math.hypot(b.center.x - clickLocal.x, b.center.y - clickLocal.y)
  )[0];
  const relativeOffset = { x: clickLocal.x - nearestRef.center.x, y: clickLocal.y - nearestRef.center.y };

  let correspondingRefLocal = null;
  if (!useStairs) {
    const otherColumnsWorld = detectColumns(targetFpMesh);
    if (otherColumnsWorld.length < 4) return null;
    const otherColumns = columnsToLocal(targetFpMesh, otherColumnsWorld);
    const floorOffset = calculateFloorOffset(currentColumns, otherColumns);
    if (!floorOffset) return null;
    correspondingRefLocal = {
      x: nearestRef.center.x + floorOffset.offsetX,
      y: nearestRef.center.y + floorOffset.offsetY,
    };
  } else {
    const otherStairs = stairsToLocal(targetFpMesh, detectStairs(targetFpMesh));
    if (!otherStairs.length) return null;
    const counterpart = findCorrespondingStair(nearestRef, otherStairs);
    if (counterpart) {
      const t = counterpart.center;
      correspondingRefLocal = { x: t.x, y: t.y };
    } else {
      const floorOffset = calculateFloorOffsetByStairs(currentStairs, otherStairs);
      if (!floorOffset) return null;
      correspondingRefLocal = {
        x: nearestRef.center.x + floorOffset.offsetX,
        y: nearestRef.center.y + floorOffset.offsetY,
      };
    }
  }

  const projectionWorld = new THREE.Vector3(
    correspondingRefLocal.x + relativeOffset.x,
    correspondingRefLocal.y + relativeOffset.y,
    0,
  );
  targetFpMesh.localToWorld(projectionWorld);
  return { x: projectionWorld.x, y: projectionWorld.y, z: 0 };
}
